import axios from 'axios';
import { graphqlRequest } from './graphqlClient.js';
import { listRequirements, listStorageLinks } from '../graphql/queries.js';

const API_BASE_CLASSIFIER = process.env.REACT_APP_API_BASE_CLASSIFIER || '';

function isDrawingFile(file) {
  if (file?.docClass === 'drawing') return true;
  return file?.category === 'Deliverable' && file?.structuralMode === 'drawing_annotation';
}

async function fetchClassifierFiles(jobId) {
  if (!jobId || !API_BASE_CLASSIFIER) return [];
  try {
    const idToken = localStorage.getItem('id_token');
    const res = await axios.get(
      `${API_BASE_CLASSIFIER}/classifier/results?jobId=${jobId}`,
      { headers: idToken ? { Authorization: `Bearer ${idToken}` } : {} }
    );
    return (res.data?.files || []).filter((f) => f.fileKey && f.fileKey !== '__meta__');
  } catch {
    return [];
  }
}

function mergeClassifierFileMetadata(cachedFiles, apiFiles) {
  if (!cachedFiles.length) return apiFiles;
  if (!apiFiles.length) return cachedFiles;
  const apiByKey = new Map(apiFiles.map((f) => [f.fileKey, f]));
  return cachedFiles.map((cached) => {
    const fromApi = apiByKey.get(cached.fileKey);
    if (!fromApi) return cached;
    return {
      ...cached,
      docClass: cached.docClass || fromApi.docClass || '',
      structuralMode: cached.structuralMode || fromApi.structuralMode || '',
      complianceType: cached.complianceType || fromApi.complianceType || '',
    };
  });
}

async function resolveFolderFiles(jobId, cachedFiles) {
  const apiFiles = jobId ? await fetchClassifierFiles(jobId) : [];
  if (cachedFiles.length > 0) {
    const needsEnrichment = cachedFiles.some(
      (f) => !f.docClass && (f.category === 'Deliverable' || f.category === 'Requirement')
    );
    return needsEnrichment ? mergeClassifierFileMetadata(cachedFiles, apiFiles) : cachedFiles;
  }
  return apiFiles;
}

/**
 * Linked classifier folders for the project, with drawing file counts.
 * @param {string} projectId
 * @returns {Promise<object[]>}
 */
export async function fetchReviewFolders(projectId) {
  if (!projectId) return [];

  let allReqs = [];
  let nextToken = null;
  do {
    const res = await graphqlRequest({
      query: listRequirements,
      variables: {
        filter: { projectId: { eq: projectId } },
        limit: 1000,
        nextToken,
      },
    });
    const { items, nextToken: nt } = res.data?.listRequirements || {};
    if (items) allReqs = allReqs.concat(items);
    nextToken = nt;
  } while (nextToken);

  const classifierReqs = allReqs.filter((req) => {
    try {
      const meta = JSON.parse(req.description || '{}');
      return meta.source === 'classifier' || meta.storage?.length > 0;
    } catch {
      return false;
    }
  });

  const folders = await Promise.all(
    classifierReqs.map(async (req) => {
      try {
        const meta = JSON.parse(req.description || '{}');
        const linkRes = await graphqlRequest({
          query: listStorageLinks,
          variables: {
            filter: { requirementId: { eq: req.id } },
            limit: 10,
          },
        });
        const links = linkRes.data?.listStorageLinks?.items || [];
        const link = links[0];
        const jobId = meta.jobId || '';
        const cachedFiles = meta.files || [];
        const files = await resolveFolderFiles(jobId, cachedFiles);
        const drawingFiles = files.filter(isDrawingFile);

        return {
          requirementId: req.id,
          linkId: link?.id || meta.storage?.[0]?.id || '',
          title: req.title || 'Linked folder',
          jobId,
          folderUrl: link?.url || meta.storage?.[0]?.url || '',
          fileCount: files.length,
          drawingCount: drawingFiles.length,
          drawingFiles,
        };
      } catch {
        return null;
      }
    })
  );

  return folders.filter(Boolean);
}
